import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Info, AlertCircle, Loader2, MapPin } from "lucide-react";
import {
  GoogleMap,
  useLoadScript,
  MarkerF,
  OverlayViewF,
} from "@react-google-maps/api";
import "./getStartedPage.css";
import progressIcon from "../../assets/progressIcon1.png";
import nextIcon from "../../assets/nextButtonIcon.png";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";
import DesktopBottomBar from "../../components/DesktopBottomBar";
import { useGlobalAlert } from "../../context/AlertContext";

import apiClient from "../../api/apiClient";
import { ENDPOINTS } from "../../api/endPoints";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";
import pawPrintIcon from "../../assets/pawIcon.svg";

// Firebase Auth & Redux imports
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider, appleProvider } from "../../firebase-config";
import { useSelector } from "react-redux";

const libraries = ["places"];
const fallbackCenter = { lat: 40.7648, lng: -73.9808 }; // Default: NYC

const interactiveMapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: "auto",
  mapTypeControl: false,
  streetViewControl: false,
};

const backgroundMapOptions = {
  disableDefaultUI: true,
  gestureHandling: "greedy",
  zoomControl: true,
  scrollwheel: true,
  disableDoubleClickZoom: false,
  draggable: true,
  styles: [
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [{ color: "#e5f5e0" }],
    },
    {
      featureType: "poi",
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "labels",
      stylers: [{ visibility: "on" }],
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const OnboardingPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();
  const { profile } = useSelector((state) => state.user);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const addressDebounceRef = useRef(null);

  const isFirstRender = useRef(true);

  const [bgMapRef, setBgMapRef] = useState(null);
  const [pinMapRef, setPinMapRef] = useState(null);

  // Check if Social Signup
  const isSocialAuth =
    formData.isSocialAuth === true || profile?.is_social_signup === 1;

  // Auto-fill form data if user is already logged in via social
  useEffect(() => {
    if (profile?.is_social_signup === 1 && !formData.email) {
      setFormData((prev) => ({
        ...prev,
        name: profile.user_name || prev.name,
        email: profile.user_email || profile.email || prev.email,
        isSocialAuth: true,
      }));
    }
  }, [profile, formData.email, setFormData]);

  const [isSocialLoading, setIsSocialLoading] = useState(false);

  const [activeAddressTab, setActiveAddressTab] = useState(() => {
    return localStorage.getItem("onboarding_active_tab") || "manual";
  });

  const [mapCenter, setMapCenter] = useState(() => {
    const saved = localStorage.getItem("onboarding_map_center");
    return saved ? JSON.parse(saved) : fallbackCenter;
  });

  const [markerPosition, setMarkerPosition] = useState(() => {
    const saved = localStorage.getItem("onboarding_marker_pos");
    return saved ? JSON.parse(saved) : null;
  });

  const [pinnedAddressLabel, setPinnedAddressLabel] = useState(() => {
    return localStorage.getItem("onboarding_pinned_label") || "";
  });

  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [nearbyDogs, setNearbyDogs] = useState([]);
  const [selectedDogId, setSelectedDogId] = useState(null);

  // Verification States
  const [isVerificationSent, setIsVerificationSent] = useState(() => {
    return localStorage.getItem("sp_verification_sent") === "true";
  });
  const [isVerifying, setIsVerifying] = useState(false);

  // Sync verification state to localStorage
  useEffect(() => {
    localStorage.setItem("sp_verification_sent", isVerificationSent);
  }, [isVerificationSent]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setIsVerificationSent(false);
    localStorage.setItem("sp_verification_sent", "false");
  }, [formData.email]);

  useEffect(() => {
    // Create a communication channel
    const channel = new BroadcastChannel("sp_verification_channel");

    channel.onmessage = (event) => {
      if (event.data === "email_verified") {
        triggerAlert("Email verified successfully!", "success");
        setIsVerifying(false);
        setIsVerificationSent(true);
        localStorage.setItem("sp_verification_sent", "true");
        navigate("/team-selection");
      }
    };
    return () => channel.close();
  }, [navigate, triggerAlert]);

  // Load Google Maps Script
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (bgMapRef && mapCenter) bgMapRef.panTo(mapCenter);
  }, [bgMapRef, mapCenter]);

  useEffect(() => {
    if (pinMapRef && mapCenter) pinMapRef.panTo(mapCenter);
  }, [pinMapRef, mapCenter]);

  const fetchNearbyDogs = useCallback(async (lat, lng) => {
    try {
      const response = await apiClient.post(ENDPOINTS.GET_NEARBY_DOGS, {
        latitude: lat,
        longitude: lng,
      });
      if (response.data?.status) setNearbyDogs(response.data.dogs || []);
    } catch (err) {
      console.error("Map Preview Load Error:", err);
    }
  }, []);

  useEffect(() => {
    if (mapCenter) fetchNearbyDogs(mapCenter.lat, mapCenter.lng);
  }, [mapCenter, fetchNearbyDogs]);

  useEffect(() => {
    const savedCenter = localStorage.getItem("onboarding_map_center");
    if (!savedCenter && "geolocation" in navigator && navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted" || result.state === "prompt") {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;
              setMapCenter({ lat, lng });
              localStorage.setItem(
                "onboarding_map_center",
                JSON.stringify({ lat, lng }),
              );
            },
            (error) => {
              console.warn("Onboarding Geolocation Error:", error);
              setMapCenter(fallbackCenter);
            },
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 },
          );
        }
      });
    }
  }, []);

  // Auto-hydrate formData from saved coordinates on page load
  useEffect(() => {
    const savedCenter = localStorage.getItem("onboarding_map_center");

    if (isLoaded && savedCenter && !formData.street) {
      try {
        const parsedCenter = JSON.parse(savedCenter);
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ location: parsedCenter }, (results, status) => {
          if (status === "OK" && results[0]) {
            const fullAddress = results[0].formatted_address;
            const parsedAddress = parseAddressComponents(
              results[0].address_components,
              fullAddress,
            );

            setFormData((prev) => {
              if (!prev.street) {
                return {
                  ...prev,
                  street: parsedAddress.street || prev.street,
                  city: parsedAddress.city || prev.city,
                  state: parsedAddress.state || prev.state,
                  zip: parsedAddress.zip || prev.zip,
                  country: parsedAddress.country || prev.country,
                };
              }
              return prev;
            });
          }
        });
      } catch (err) {
        console.warn("Failed to parse saved map center", err);
      }
    }
  }, [isLoaded]);

  const formatDistance = (miles = 0) => {
    const blocks = Math.round(miles * 20);
    if (blocks <= 20) {
      return `${blocks} ${blocks === 1 ? "block" : "blocks"} away`;
    }
    const roundedMiles = Number(miles.toFixed(1));
    return `${roundedMiles} ${roundedMiles === 1 ? "mile" : "miles"} away`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Helper to parse Google Maps address components into form fields with fallback
  const parseAddressComponents = (components, formattedAddress = "") => {
    let streetNum = "",
      route = "",
      city = "",
      state = "",
      zip = "",
      country = "";

    components.forEach((component) => {
      const types = component.types;
      if (types.includes("street_number")) streetNum = component.long_name;
      if (types.includes("route")) route = component.long_name;
      if (
        types.includes("locality") ||
        types.includes("sublocality") ||
        types.includes("administrative_area_level_2")
      ) {
        if (!city) city = component.long_name;
      }
      if (types.includes("administrative_area_level_1"))
        state = component.short_name;
      if (types.includes("postal_code")) zip = component.long_name;
      if (types.includes("country")) country = component.long_name;
    });

    let street = `${streetNum} ${route}`.trim();
    if (!street && formattedAddress) {
      street = formattedAddress.split(",")[0].trim();
    }

    return { street, city, state, zip, country };
  };

  const fetchCoordsFromAddress = useCallback(
    (address, shouldAutofill = true) => {
      if (!window.google || !address.trim()) return;

      setIsFetchingLocation(true);
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address: address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          localStorage.setItem(
            "onboarding_map_center",
            JSON.stringify({ lat, lng }),
          );
          localStorage.setItem(
            "onboarding_marker_pos",
            JSON.stringify({ lat, lng }),
          );

          const fullAddress = results[0].formatted_address;
          setPinnedAddressLabel(fullAddress);
          localStorage.setItem("onboarding_pinned_label", fullAddress);

          if (shouldAutofill) {
            const parsedAddress = parseAddressComponents(
              results[0].address_components,
              fullAddress,
            );

            setFormData((prev) => ({
              ...prev,
              street: parsedAddress.street || prev.street,
              city: parsedAddress.city || prev.city,
              state: parsedAddress.state || prev.state,
              zip: parsedAddress.zip || prev.zip,
              country: parsedAddress.country || prev.country,
            }));
          }
        } else {
          console.error("Geocoder failed due to: " + status);
          if (shouldAutofill) {
            triggerAlert(
              "Could not find that address. Please try again.",
              "error",
            );
          }
        }
        setIsFetchingLocation(false);
      });
    },
    [setFormData, triggerAlert],
  );

  useEffect(() => {
    if (activeAddressTab !== "manual") return;

    const street = formData.street || "";
    const city = formData.city || "";
    const state = formData.state || "";
    const country = formData.country || "";

    if (street.length > 3 && city.length > 2) {
      const fullAddress = `${street}, ${city}, ${state}, ${country}`.trim();

      if (addressDebounceRef.current) {
        clearTimeout(addressDebounceRef.current);
      }

      addressDebounceRef.current = setTimeout(() => {
        fetchCoordsFromAddress(fullAddress, false);
      }, 1500);
    }
  }, [
    formData.street,
    formData.city,
    formData.state,
    formData.country,
    activeAddressTab,
    fetchCoordsFromAddress,
  ]);

  const handleGetCurrentLocation = () => {
    if ("geolocation" in navigator) {
      setIsFetchingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setMapCenter({ lat, lng });
          localStorage.setItem(
            "onboarding_map_center",
            JSON.stringify({ lat, lng }),
          );

          setMarkerPosition({ lat, lng });
          localStorage.setItem(
            "onboarding_marker_pos",
            JSON.stringify({ lat, lng }),
          );

          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
              const fullAddress = results[0].formatted_address;
              const parsedAddress = parseAddressComponents(
                results[0].address_components,
                fullAddress,
              );

              setFormData((prev) => ({
                ...prev,
                street: parsedAddress.street || prev.street,
                city: parsedAddress.city || prev.city,
                state: parsedAddress.state || prev.state,
                zip: parsedAddress.zip || prev.zip,
                country: parsedAddress.country || prev.country,
              }));

              setPinnedAddressLabel(fullAddress);
              localStorage.setItem("onboarding_pinned_label", fullAddress);
            }

            setIsFetchingLocation(false);
          });
        },
        (error) => {
          console.error("Error getting location", error);
          setIsFetchingLocation(false);
          triggerAlert(
            "Location services are disabled! Please turn them on in settings.",
            "error",
          );
          setMapCenter(fallbackCenter);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 },
      );
    } else {
      alert("Geolocation is not supported by your browser");
    }
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMarkerPosition({ lat, lng });
    localStorage.setItem("onboarding_marker_pos", JSON.stringify({ lat, lng }));

    setMapCenter({ lat, lng });
    localStorage.setItem("onboarding_map_center", JSON.stringify({ lat, lng }));

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const fullAddress = results[0].formatted_address;
        setPinnedAddressLabel(fullAddress);
        localStorage.setItem("onboarding_pinned_label", fullAddress);

        const parsedAddress = parseAddressComponents(
          results[0].address_components,
          fullAddress,
        );
        setFormData((prev) => ({
          ...prev,
          street: parsedAddress.street || prev.street,
          city: parsedAddress.city || prev.city,
          state: parsedAddress.state || prev.state,
          zip: parsedAddress.zip || prev.zip,
          country: parsedAddress.country || prev.country,
        }));
      }
    });
  };

  const handleTabSwitch = (tab) => {
    setActiveAddressTab(tab);
    localStorage.setItem("onboarding_active_tab", tab);

    if (tab === "pin" && !pinnedAddressLabel && formData.street) {
      const combinedAddress =
        `${formData.street}, ${formData.city}, ${formData.state}, ${formData.country}`
          .replace(/,\s*,/g, ",")
          .trim();
      setPinnedAddressLabel(combinedAddress);
    }
  };

  const handlePinnedAddressChange = (value) => {
    setPinnedAddressLabel(value);

    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }

    if (!value.trim()) return;

    addressDebounceRef.current = setTimeout(() => {
      if (value.trim().length >= 8) {
        fetchCoordsFromAddress(value, true);
      }
    }, 1500);
  };

  useEffect(() => {
    return () => {
      if (addressDebounceRef.current) {
        clearTimeout(addressDebounceRef.current);
      }
    };
  }, []);

  // Validation Variables Updates
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

  const isPasswordLengthValid =
    isSocialAuth ||
    (formData?.password?.length >= 8 && formData?.password?.length <= 20);
  const hasNumberOrSpecial =
    isSocialAuth || /[0-9!@#$%^&*(),.?":{}|<>]/.test(formData.password);
  const passwordsMatch =
    isSocialAuth ||
    (formData.password &&
      formData.confirmPassword &&
      formData.password === formData.confirmPassword);

  const isAddressValid =
    activeAddressTab === "pin"
      ? (pinnedAddressLabel && pinnedAddressLabel.length > 3) ||
        (formData.street && formData.city)
      : formData.street && formData.city;

  const isFormFilled = isSocialAuth
    ? formData.name && formData.email && isAddressValid
    : formData.name &&
      formData.email &&
      formData.password &&
      formData.confirmPassword &&
      isAddressValid;

  const isFormValid =
    isFormFilled &&
    isEmailValid &&
    passwordsMatch &&
    isPasswordLengthValid &&
    hasNumberOrSpecial;

  const executeNextStep = async () => {
    // If Social login user, skip OTP verification entirely
    if (isSocialAuth) {
      navigate("/team-selection");
      return;
    }

    setIsVerifying(true);
    try {
      if (!isVerificationSent) {
        await apiClient.post("/emailVerification/send-verification", {
          email: formData.email,
        });

        triggerAlert(
          "Verification email sent! Please check your inbox and verify your email.",
          "success",
        );
        setIsVerificationSent(true);
      } else {
        const response = await apiClient.get(
          `/emailVerification/check-verification?email=${encodeURIComponent(
            formData.email,
          )}`,
        );
        const { status, is_verified } = response.data || {};

        if (status === true && is_verified === true) {
          navigate("/team-selection");
        } else {
          triggerAlert("Please verify your email address.", "error");
        }
      }
    } catch (error) {
      console.error("Verification API Error:", error);
      if (isVerificationSent) {
        triggerAlert("Please verify your email address.", "error");
      } else {
        triggerAlert(
          error.response?.data?.message ||
            "Failed to send verification email. Please try again.",
          "error",
        );
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleNext = async () => {
    if (!isFormValid || isVerifying) return;

    if (activeAddressTab === "pin" && (!formData.street || !formData.city)) {
      setIsVerifying(true);

      if (window.google && pinnedAddressLabel) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: pinnedAddressLabel }, (results, status) => {
          if (status === "OK" && results[0]) {
            const fullAddress = results[0].formatted_address;
            const parsedAddress = parseAddressComponents(
              results[0].address_components,
              fullAddress,
            );
            setFormData((prev) => ({
              ...prev,
              street: parsedAddress.street || prev.street,
              city: parsedAddress.city || prev.city,
              state: parsedAddress.state || prev.state,
              zip: parsedAddress.zip || prev.zip,
              country: parsedAddress.country || prev.country,
            }));
            executeNextStep();
          } else {
            triggerAlert(
              "Please allow the address to finish loading.",
              "error",
            );
            setIsVerifying(false);
          }
        });
      } else {
        setIsVerifying(false);
      }
      return;
    }
    executeNextStep();
  };

  const handleBackNavigation = () => {
    setFormData((prev) => ({
      ...prev,
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      street: "",
      apartment: "",
      city: "",
      state: "",
      zip: "",
      isSocialAuth: false,
    }));
    navigate("/");
  };

  return (
    <div className="sp-container">
      <MobileTopBar
        onBack={handleBackNavigation}
        backColor="#fff"
        logoColor="#fff"
        isProtectedRoute={false}
      />

      <div className="sp-onboarding-form-section">
        {/* DESKTOP TOP BAR */}
        <DesktopTopBar
          showBack={true}
          onBack={handleBackNavigation}
          backColor="#fff"
          showProgress={true}
          progressIconSrc={progressIcon}
          progressPercent={0}
          iconLeft={10}
          iconWidth="90px"
        />

        <div className="sp-form-content-wrapper">
          <div className="sp-onboarding-header">
            <h2 className="sp-onboarding-title">LET'S GET STARTED!</h2>

            <div className="sp-form-group">
              <label className="sp-label">What's your name?</label>
              <input
                type="text"
                name="name"
                placeholder="Your full name"
                className={`sp-input-glass ${formData.name ? "has-value" : ""}`}
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>

            <div className="sp-form-group">
              <label className="sp-label">Email</label>
              <input
                type="email"
                name="email"
                placeholder="email@example.com"
                className={`sp-input-glass ${formData.email ? "has-value" : ""}`}
                value={formData.email}
                onChange={handleInputChange}
                readOnly={isSocialAuth}
                style={isSocialAuth ? { opacity: 0.85, cursor: "default" } : {}}
              />
            </div>

            {/* ONLY RENDER PASSWORD FIELDS IF IT'S NOT A SOCIAL SIGNUP */}
            {!isSocialAuth && (
              <>
                <div className="sp-form-group">
                  <label className="sp-label">Choose Password</label>
                  <div className="sp-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Min. 8 chars"
                      className={`sp-input-glass ${formData.password ? "has-value" : ""}`}
                      value={formData.password}
                      onChange={handleInputChange}
                    />
                    <button
                      type="button"
                      className="sp-eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                  </div>
                </div>

                <div className="sp-form-group">
                  <label className="sp-label">Confirm Password</label>
                  <div className="sp-input-wrapper">
                    <input
                      type={showConfirm ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Passwords must match"
                      className={`sp-input-glass ${
                        formData.confirmPassword ? "has-value" : ""
                      } ${
                        !passwordsMatch && formData?.confirmPassword?.length > 0
                          ? "input-error"
                          : ""
                      }`}
                      value={formData?.confirmPassword}
                      onChange={handleInputChange}
                    />
                    <button
                      type="button"
                      className="sp-eye-btn"
                      onClick={() => setShowConfirm(!showConfirm)}
                    >
                      {showConfirm ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                  </div>
                  {!passwordsMatch && formData?.confirmPassword?.length > 0 && (
                    <p className="sp-error-text">
                      <Info size={18} fill="#FACC15" stroke={"#009FDC"} />
                      Passwords do not match
                    </p>
                  )}
                </div>

                <div className="cp-validation-list">
                  <div
                    className={`cp-valid-item ${passwordsMatch ? "met" : ""}`}
                  >
                    <AlertCircle
                      size={16}
                      fill={passwordsMatch ? "#47D7AC" : "transparent"}
                      stroke={"#009FDC"}
                    />
                    <span>Passwords must match</span>
                  </div>

                  <div
                    className={`cp-valid-item ${isPasswordLengthValid ? "met" : ""}`}
                  >
                    <AlertCircle
                      size={16}
                      fill={isPasswordLengthValid ? "#47D7AC" : "transparent"}
                      stroke={"#009FDC"}
                    />
                    <span>Must be 8-20 characters</span>
                  </div>

                  <div
                    className={`cp-valid-item ${hasNumberOrSpecial ? "met" : ""}`}
                  >
                    <AlertCircle
                      size={16}
                      fill={hasNumberOrSpecial ? "#47D7AC" : "transparent"}
                      stroke={"#009FDC"}
                    />
                    <span>
                      Must include at least one number or one special character
                      (e.g $%&)
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="sp-onboarding-map-section">
        {/* Main Interactive Google Map */}
        <div className="sp-map-container">
          {isLoaded ? (
            <GoogleMap
              onLoad={(map) => setBgMapRef(map)}
              mapContainerStyle={{
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
              }}
              center={mapCenter}
              zoom={14}
              options={backgroundMapOptions}
              onClick={() => setSelectedDogId(null)}
            >
              {/* USER LOCATION PIN */}
              {(markerPosition || mapCenter) && (
                <OverlayViewF
                  position={markerPosition || mapCenter}
                  mapPaneName="overlayMouseTarget"
                >
                  <div
                    className="user-location-pin scale-07"
                    style={{ maxWidth: "none", minWidth: "100px" }}
                  >
                    <div className="pulse-ring" />
                    <MapPin
                      size={24}
                      color="rgba(0, 155, 206, 1)"
                      fill="white"
                    />
                  </div>
                </OverlayViewF>
              )}

              {/* NEARBY DOGS PINS */}
              {nearbyDogs.map((dog) => {
                const isSelected = selectedDogId === dog.id;

                return (
                  <OverlayViewF
                    key={dog.id}
                    position={{
                      lat: parseFloat(dog.user_latitude),
                      lng: parseFloat(dog.user_longitude),
                    }}
                    mapPaneName="overlayMouseTarget"
                  >
                    <div
                      style={{
                        position: "relative",
                        zIndex: isSelected ? 100 : 1,
                      }}
                    >
                      <div
                        className={`onboadring-dog-pin ${isSelected ? "onboadring-pin-active" : ""}`}
                        style={{
                          transform: isSelected
                            ? "translate(-50%, -100%) scale(1.15)"
                            : "translate(-50%, -100%) scale(0.8)",
                          transition: "transform 0.25s ease-in-out",
                          cursor: "pointer",
                          boxShadow: isSelected
                            ? "0px 8px 16px rgba(0,0,0,0.3)"
                            : "none",
                          maxWidth: "none",
                          minWidth: "50px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDogId(isSelected ? null : dog.id);
                        }}
                      >
                        <img
                          src={
                            (dog.team_social === 1
                              ? dog.profile_image?.[0] ||
                                dog.user_profile_image?.[0]
                              : dog.dog_images?.[0] ||
                                dog.profile_image?.[0]) ||
                            (dog.team_social === 1
                              ? userPlaceholder
                              : dogPlaceholder)
                          }
                          alt="dog"
                          style={{ maxWidth: "none" }}
                          onError={(e) => {
                            e.target.src =
                              dog.team_social === 1
                                ? userPlaceholder
                                : dogPlaceholder;
                          }}
                        />
                      </div>

                      {/* SIMPLIFIED INFO CARD */}
                      {isSelected && (
                        <div
                          className="onboadring-dog-info-card top"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="onboadring-card-main-row">
                            <div className="onboadring-map-info-card-dog-avatar">
                              <img
                                src={
                                  (dog.team_social === 1
                                    ? dog.profile_image?.[0] ||
                                      dog.user_profile_image?.[0]
                                    : dog.dog_images?.[0] ||
                                      dog.profile_image?.[0]) ||
                                  (dog.team_social === 1
                                    ? userPlaceholder
                                    : dogPlaceholder)
                                }
                                alt={dog.dog_name || "Dog"}
                                onError={(e) => {
                                  e.target.src =
                                    dog.team_social === 1
                                      ? userPlaceholder
                                      : dogPlaceholder;
                                }}
                                style={{ maxWidth: "none" }}
                              />
                            </div>

                            <div className="onboadring-card-right-column">
                              <h2 className="onboadring-card-dog-name">
                                {dog.team_social === 1
                                  ? dog.user_name
                                  : dog.dog_name}
                              </h2>

                              <div className="onboadring-card-distance-badge">
                                <span className="onboadring-paw-icon">
                                  <img
                                    src={pawPrintIcon}
                                    alt="paw"
                                    className="onboadring-distance-paw-icon"
                                    style={{ maxWidth: "none" }}
                                  />
                                </span>
                                {Math.round(dog.distance_miles * 20) <= 20
                                  ? `${Math.round(dog.distance_miles * 20)} blocks away`
                                  : `${Number(dog.distance_miles.toFixed(1))} miles away`}
                              </div>
                            </div>
                          </div>
                          <div className="onboadring-card-arrow" />
                        </div>
                      )}
                    </div>
                  </OverlayViewF>
                );
              })}
            </GoogleMap>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                background: "#f3f4f6",
              }}
            >
              <Loader2 className="animate-spin" size={32} color="#009fd9" />
            </div>
          )}
        </div>

        {/* Address Card */}
        <div className="sp-address-card">
          <div className="sp-address-content">
            {/* TABS HEADER */}
            <div className="sp-address-tabs">
              <button
                className={`sp-tab-btn ${activeAddressTab === "manual" ? "active" : ""}`}
                onClick={() => handleTabSwitch("manual")}
              >
                Enter your address below
              </button>
              <button
                className={`sp-tab-btn ${activeAddressTab === "pin" ? "active" : ""}`}
                onClick={() => handleTabSwitch("pin")}
              >
                Pin Your Location
              </button>
            </div>

            {/* TAB 1: MANUAL & GET CURRENT LOCATION */}
            {activeAddressTab === "manual" ? (
              <div className="sp-address-container">
                <button
                  type="button"
                  className="sp-get-location-text"
                  onClick={handleGetCurrentLocation}
                  disabled={isFetchingLocation}
                >
                  {isFetchingLocation
                    ? "Fetching location..."
                    : "Get Your Current Location"}
                </button>

                <div className="sp-address-input-wrapper">
                  <input
                    type="text"
                    name="street"
                    className={`sp-address-input ${formData.street ? "has-value" : ""}`}
                    placeholder="Street Address"
                    value={formData.street}
                    onChange={handleInputChange}
                  />

                  <input
                    type="text"
                    name="apartment"
                    className={`sp-address-input ${formData.apartment ? "has-value" : ""}`}
                    placeholder="Apartment, suite, unit, etc. (optional)"
                    value={formData.apartment}
                    onChange={handleInputChange}
                  />

                  <div className="sp-address-row desktop-three-col">
                    <input
                      type="text"
                      name="city"
                      className={`sp-address-input ${formData.city ? "has-value" : ""}`}
                      placeholder="City"
                      value={formData.city}
                      onChange={handleInputChange}
                    />

                    <input
                      type="text"
                      name="state"
                      className={`sp-address-input ${formData.state ? "has-value" : ""}`}
                      placeholder="State"
                      value={formData.state}
                      onChange={handleInputChange}
                    />

                    <input
                      type="text"
                      name="zip"
                      className={`sp-address-input ${formData.zip ? "has-value" : ""}`}
                      placeholder="ZIP"
                      value={formData.zip}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* TAB 2: PIN YOUR LOCATION */
              <div className="sp-address-container">
                <div
                  className="sp-map-pin-view"
                  style={{
                    border: "none",
                    background: "#e5e7eb",
                    position: "relative",
                  }}
                >
                  {!isLoaded ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <Loader2
                        className="animate-spin"
                        size={32}
                        color="#009fd9"
                      />
                    </div>
                  ) : (
                    <GoogleMap
                      onLoad={(map) => setPinMapRef(map)}
                      mapContainerStyle={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "1rem",
                      }}
                      center={mapCenter}
                      zoom={14}
                      options={interactiveMapOptions}
                      onClick={handleMapClick}
                    >
                      {markerPosition && <MarkerF position={markerPosition} />}
                    </GoogleMap>
                  )}

                  {isLoaded && !markerPosition && (
                    <div className="sp-map-instructions">
                      Drag map and click to pin location
                    </div>
                  )}
                </div>

                <div
                  className="sp-pinned-address-display"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "0.6rem",
                    width: "100%",
                  }}
                >
                  <label
                    style={{
                      fontSize: "0.85rem",
                      opacity: 0.8,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Address:
                  </label>

                  {isFetchingLocation ? (
                    <span>Updating address...</span>
                  ) : (
                    <input
                      type="text"
                      value={pinnedAddressLabel}
                      onChange={(e) =>
                        handlePinnedAddressChange(e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (addressDebounceRef.current) {
                            clearTimeout(addressDebounceRef.current);
                          }
                          fetchCoordsFromAddress(pinnedAddressLabel, true);
                        }
                      }}
                      placeholder="Type an address or click the map..."
                      style={{
                        flex: 1,
                        minWidth: 0,
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        color: "inherit",
                        fontSize: "inherit",
                        fontWeight: "inherit",
                        padding: 0,
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* COMMON FOOTER */}
            <div className="sp-address-footer">
              <div>
                <p className="sp-helper-text">
                  Other users will only see your neighborhood name
                </p>
                <p className="sp-helper-sub">You can change this anytime</p>
              </div>
            </div>

            {/* DESKTOP BOTTOM BAR */}
            <DesktopBottomBar
              onNext={handleNext}
              isNextEnabled={isFormValid}
              isLoading={isVerifying}
              nextIconSrc={nextIcon}
              variant="green"
            />

            <MobileBottomBar
              onNext={handleNext}
              isNextEnabled={isFormValid && !isVerifying}
              nextIconSrc={nextIcon}
              variant="green"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
